import { DataShape, FormProps, getFormAsyncErrors, getFormValues } from 'redux-form'
import * as isNumber from 'lodash/isNumber'
import * as numeral from 'numeral'
import { submitFormActionCreator } from 'actions/formActions'
import resolveErrorById, { ValidationErrorKeysWithoutArgs } from 'constants/errors'
import { ErrorItem, ErrorItemState, FormErrorsResponse, ReduxFormProps, SignedCurrencyFieldState, StoreDispatch } from 'interfaces/component'
import { BasicFormFieldAction } from 'interfaces/action'
import { State } from 'interfaces/state'
import { CURRENCY_OPTIONAL_DECIMALS_FORMAT } from 'constants/forms'
import * as isArray from 'lodash/isArray'
import * as toPath from 'lodash/toPath'
import { AnyDict } from 'interfaces'
import { ButtonProps } from 'components/features/pfm/Button/Button'
import { HousingType } from 'interfaces/property'
import { LinkageFormField } from 'interfaces/linkage'
import { FormNameType } from 'interfaces/form'
import { FutureLayer } from 'components/features/future/types'
import { FormState } from 'react-hook-form'

/** Turn a back-end format error into redux-form format (string arrays are stringified).
 *
 * FROM:
 * ```
 * {
 *   income: ['Not valid income'],
 *   homes: [
 *     { zip5: ['Not valid zip5'] },
 *     { zip4: ['Not valid zip4'] }
 *   ]
 * }
 * ```
 *
 * TO:
 * ```
 * {
 *   income: "['Not valid income']",
 *   homes: [
 *     { zip5: "['Not valid zip5']" },
 *     { zip4: "['Not valid zip4']" }
 *   ]
 * }
 * ```
 */
export const packFormErrors = (errors: (string | FormErrorsResponse)[]): string | Record<string, string>[] => {
  const isAllStrings = (errors as string[]).every((error) => typeof error === 'string')

  return isAllStrings
    ? JSON.stringify(errors)
    : (errors as Record<string, string[]>[]).map(
      (error) => Object.keys(error)
        .reduce((result, key) => ({
          ...result,
          [key]: packFormErrors(error[key]),
        }), {})
    )
}

export const unpackFormErrors = (errorString: string | undefined): string[] => {
  if (!errorString) {
    return []
  }

  const firstChar = errorString[0]
  let errors: string[]

  // this is JSONed array
  if (firstChar === '[') {
    errors = JSON.parse(errorString)
  } else {
    errors = (isArray(errorString) ? errorString : [errorString]) as string[]
  }

  return errors.map((errorId: ValidationErrorKeysWithoutArgs) => {
    return resolveErrorById(errorId)
  })
}

export const prepareFieldErrors = (error: string | undefined, touched: boolean): ErrorItem[] => {
  const errorList = unpackFormErrors(error)

  if (!touched || !errorList.length) {
    return []
  }

  return (errorList as string[]).map((e) => ({ state: 'errored' as ErrorItemState, text: e }))
}

export const buildFieldArrayValuePath = (arrayName: string, index: number, fieldName: string) =>
  `${ arrayName }[${ index }].${ fieldName }`

export const isFieldArrayFieldActive = (arrayName: string, index: number, fieldName: string, activeField: string): boolean =>
  activeField.includes(buildFieldArrayValuePath(arrayName, index, fieldName))

export type ArrayFieldDetails = {
  index: number
  field: string
}
export const getArrayFieldDetails = (field: string, arrayName: string): ArrayFieldDetails | null => {
  const fieldPath = toPath(field)
  if (fieldPath.length !== 3 || fieldPath[0] !== arrayName) {
    return null
  }
  return {
    index: parseInt(fieldPath[1], 10),
    field: fieldPath[2],
  }
}

export const getFormArrayValueFromAction = (action: BasicFormFieldAction, formName: string, arrayName: string, state: State) => {
  if (action.meta.form !== formName) {
    return null
  }
  const data = getArrayFieldDetails(action.meta.field, arrayName)
  if (!data) {
    return null
  }

  const formValues = getFormValues(formName)(state) as any
  const asyncErrors = getFormAsyncErrors(formName)(state) as any
  const arrayAsyncErrors = (asyncErrors || {})[arrayName]

  return {
    index: data.index,
    field: data.field,
    values: (formValues[arrayName] as any[])[data.index],
    asyncErrors: arrayAsyncErrors && data.index < arrayAsyncErrors.length
      ? arrayAsyncErrors[data.index]
      : {},
  }
}


export const onFormSubmit = (values: AnyDict, dispatch: StoreDispatch, form: FormProps<AnyDict, DataShape, State>) => {
  const action = submitFormActionCreator(
    form.form as FormNameType, values
  )
  dispatch(action)
  return {}
}

export const shouldAsyncValidate = ({ trigger }: { trigger: string }) =>
  trigger === 'submit' || trigger === 'blur'


export const getSaveButtonFormProps =
  ({ submitting }: ReduxFormProps): Partial<ButtonProps> => ({
    isSubmitting: submitting,
    qaMarker: 'save-button',
  })

export const parseCurrency = (input: string): number | null => {
  if (input === '') {
    return null
  }

  return numeral(input).value()
}

export const formatCurrency = (value: number | null): string =>
  !isNumber(value) ? '' : numeral(value).format(CURRENCY_OPTIONAL_DECIMALS_FORMAT)

// new, more complicated normalizer
export const parseSignedCurrency = (input: string): SignedCurrencyFieldState => {
  const value = parseCurrency(input)
  return {
    sign: input.slice(0, 1) === '-' ? -1 : 1,
    absValue: value ? Math.abs(value) : value,
  }
}

export const formatSignedCurrency = (value?: SignedCurrencyFieldState): string => {
  if (!value) {
    return ''
  }
  const { sign, absValue } = value
  const signPart = sign < 0 ? '-' : ''
  const valuePart = absValue === null ? '' : absValue

  return signPart + valuePart
}

/**
 * Due to the fact the form is generated we can't rely on field names, they may break our logic.
 * So we replace original field names with well-known and vice-versa on submit.
 * @param fields
 */
export const getMangledFieldNames = (fields: LinkageFormField[]): Record<string, string> =>
  fields.reduce(
    (result, { name }, idx) => ({ ...result, [name]: `field${ idx }` }),
    {}
  )

export const validateDecimalCurrency = (value: string) => {
  const split = value.split('.')
  const decimals = split[1]
  let valid = true
  if (typeof decimals === 'string') {
    valid = valid && /\d{1,2}/.test(decimals)
  }

  valid = valid && numeral(split[0]).format('$0,0') === split[0]

  return valid
}

export const validateNotZeroCurrency = (value: string): boolean => {
  return numeral(value).value() !== 0
}

const HOUSING_TYPES_WITH_ZIP: HousingType[] = ['rent', 'other']
export const isHousingTypeWithZip = (housingType: HousingType) =>
  HOUSING_TYPES_WITH_ZIP.includes(housingType)

/* Workaround redux-form issue that submits empty string instead of boolean */
/* https://github.com/erikras/redux-form/issues/3162 */
export const parseValueToBoolean = (value: boolean | string) => !!value

export function differenceProps <O, N extends Record<keyof O, any>> (oldObj: Partial<O>, newObj: Partial<N>): Partial<O> {
  const keys = Object.keys(oldObj) as (keyof O)[]
  return keys.reduce((acc, key) => {
    if (oldObj[key] !== newObj[key] && newObj[key] !== undefined) {
      return {
        ...acc,
        [key]: newObj[key],
      }
    }

    return acc
  }, {})
}


export const validateRoutingNumber = (routing: string) => {
  if (routing.length !== 9) {
    return false
  }

  // http://en.wikipedia.org/wiki/Routing_transit_number#MICR_Routing_number_format
  const checksumTotal =
    (7 * (parseInt(routing.charAt(0), 10) + parseInt(routing.charAt(3), 10) + parseInt(routing.charAt(6), 10))) +
    (3 * (parseInt(routing.charAt(1), 10) + parseInt(routing.charAt(4), 10) + parseInt(routing.charAt(7), 10))) +
    (9 * (parseInt(routing.charAt(2), 10) + parseInt(routing.charAt(5), 10) + parseInt(routing.charAt(8), 10)))

  const checksumMod = checksumTotal % 10

  return checksumMod === 0
}

export const getProgressBarWidth = (formState: FormState<FutureLayer>, defaultPercent?: number) => {
  const { isSubmitting, isSubmitSuccessful } = formState
  switch (true) {
    case isSubmitting: return 100
    case isSubmitSuccessful: return 100
    default: return defaultPercent || 0
  }
}
